/**
 * JS error capture for the Observ RUM SDK (FR-9, story 2.4).
 *
 * Captures uncaught errors (`window` `error`) and unhandled promise rejections
 * (`unhandledrejection`) and emits them as OTel log records
 * (`observ.session.js_error`) on the SAME logs channel as the semantic events
 * (story 2.3): standard exception semconv attributes + `session.id`. The stack
 * trace is carried as a log/event attribute — never a metric (architecture
 * enforcement).
 *
 * Invariant (1.1/1.2/1.3): never break the host page. Handlers run when the
 * browser is already handling an error, so each is `try/catch`-guarded and we
 * NEVER `preventDefault()` — the site's own error flow (console, other handlers)
 * is left untouched; we only observe.
 */
import type { Logger } from '@opentelemetry/api-logs'
import {
  ATTR_EXCEPTION_MESSAGE,
  ATTR_EXCEPTION_STACKTRACE,
  ATTR_EXCEPTION_TYPE,
} from '@opentelemetry/semantic-conventions'

import { emitSessionEvent, setupOtelLogs } from './otel-logs.js'
import type { ObservInitOptions } from './types.js'

/** Max `js_error` events per rolling window — bounds an error storm (AC#5)
 *  without going permanently blind: the window resets, so capture self-heals. */
export const MAX_JS_ERRORS = 50
/** Rolling window (ms) for the {@link MAX_JS_ERRORS} rate limit. */
export const RATE_WINDOW_MS = 60_000
/** Truncation bounds so one error can't produce a huge log record. */
const MAX_MESSAGE = 1_024
const MAX_STACK = 4_096

/** Best-effort string for a rejection reason: Error→message handled by the
 *  caller; objects → JSON (so `{code:500}` isn't flattened to "[object Object]");
 *  everything else → `String`. */
function reasonToMessage(reason: unknown): string {
  if (typeof reason === 'string') return reason
  if (typeof reason === 'object' && reason !== null) {
    try {
      return JSON.stringify(reason) ?? String(reason)
    } catch {
      return String(reason)
    }
  }
  return String(reason)
}

/** Public handle for the running JS-error capture. */
export interface JsErrorHandle {
  /** Remove the global error listeners. Idempotent. The shared logs provider is
   *  shut down by `index.shutdown()`, NOT here (two taps share it). */
  stop(): void
}

const NOOP: JsErrorHandle = { stop: () => {} }

/** Module-level guard so the capture is self-idempotent. */
let active: JsErrorHandle | null = null

function truncate(s: string, max: number): string {
  return s.length > max ? s.slice(0, max) : s
}

/**
 * Start capturing uncaught JS errors + unhandled rejections. Returns a handle
 * whose `stop()` removes the listeners. Never throws; no-op in a non-DOM env.
 * Not gated by `disableReplay` — errors are useful even without replay.
 */
export function startJsErrorCapture(options: ObservInitOptions): JsErrorHandle {
  if (active) return active
  if (typeof window === 'undefined') return NOOP

  let logger: Logger
  try {
    logger = setupOtelLogs(options) // shared, idempotent — same logger as 2.3
  } catch (err) {
    console.warn('[observ] js-errors: logs setup failed', err)
    return NOOP
  }

  // Sliding-window rate limit: at most MAX_JS_ERRORS per RATE_WINDOW_MS. Resets
  // each window so a burst at boot doesn't blind capture for the rest of a long
  // SPA session.
  let windowStart = 0
  let inWindow = 0

  const emit = (type: string | undefined, message: string, stack: string | undefined): void => {
    const now = Date.now()
    if (now - windowStart >= RATE_WINDOW_MS) {
      windowStart = now
      inWindow = 0
    }
    if (inWindow >= MAX_JS_ERRORS) return // anti-flood cap (AC#5)
    inWindow++
    const attrs: Record<string, string> = {
      [ATTR_EXCEPTION_MESSAGE]: truncate(message, MAX_MESSAGE),
    }
    if (type) attrs[ATTR_EXCEPTION_TYPE] = type
    if (stack) attrs[ATTR_EXCEPTION_STACKTRACE] = truncate(stack, MAX_STACK)
    emitSessionEvent(logger, 'observ.session.js_error', attrs)
  }

  const onError = (e: ErrorEvent): void => {
    try {
      // `e.error` is the Error in modern browsers; cross-origin "Script error."
      // has `e.error === null`, so fall back to `e.message` with no stack.
      const err = e.error as Error | null | undefined
      emit(err?.name, err?.message ?? e.message ?? 'unknown error', err?.stack ?? undefined)
    } catch {
      /* never let the error handler break the host page */
    }
  }

  const onRejection = (e: PromiseRejectionEvent): void => {
    try {
      const reason: unknown = e.reason
      if (reason instanceof Error) {
        emit(reason.name, reason.message, reason.stack ?? undefined)
      } else {
        emit('UnhandledRejection', reasonToMessage(reason), undefined)
      }
    } catch {
      /* swallow — observe, never interfere */
    }
  }

  // Do NOT use `window.onerror =` (would clobber a host handler). Never call
  // preventDefault — we observe without suppressing the site's error flow.
  window.addEventListener('error', onError)
  window.addEventListener('unhandledrejection', onRejection)

  const handle: JsErrorHandle = {
    stop(): void {
      window.removeEventListener('error', onError)
      window.removeEventListener('unhandledrejection', onRejection)
      active = null
    },
  }
  active = handle
  return handle
}
